import { apiFetch } from '@/lib/apiFetch';
import { hasManualTradingOrigin, manualTradingApiUrl } from '@/config/api';

export type LoadMeResponse = {
  user: {
    user_id: string;
    username: string;
    email: string;
    roles: string[];
  };
  exchange_accounts: ExchangeAccountView[];
  exchange_leverage_limits?: ExchangeLeverageLimitView[];
};

export type ExchangeLeverageLimitView = {
  exchange: string;
  max_leverage: number;
};

export type ExchangeAccountView = {
  exchange: string;
  api_key_label: string;
  category: string;
  status: string;
};

export type SaveExchangeAccountBody = {
  exchange: string;
  api_key: string;
  secret: string;
  passphrase?: string;
  category: 'Spot' | 'Linear' | 'Inverse';
};

export class ManualTradingMisconfiguredError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ManualTradingMisconfiguredError';
  }
}

function isManualTradingMisconfigured(status: number, body: string): boolean {
  if (status === 503) {
    return true;
  }
  const lower = body.toLowerCase();
  return (
    lower.includes('jwt not configured') ||
    lower.includes('jwt secret not configured') ||
    lower.includes('jwt_verify.secret')
  );
}

async function parseApiError(res: Response): Promise<string> {
  const text = await res.text();
  try {
    const j = JSON.parse(text) as { message?: string };
    if (j?.message) return j.message;
  } catch {
    /* raw */
  }
  return text || res.statusText;
}

/** 使用 auth-service 下发的 access token，初始化 trading-engine 侧 runtime 与交易所私网客户端。 */
export async function loadManualTradingSession(): Promise<LoadMeResponse> {
  const res = await apiFetch(manualTradingApiUrl('/loadme'));
  if (!res.ok) {
    const text = await res.text();
    let message = text;
    try {
      const j = JSON.parse(text) as { message?: string };
      if (j?.message) {
        message = j.message;
      }
    } catch {
      /* raw text */
    }
    if (isManualTradingMisconfigured(res.status, message)) {
      throw new ManualTradingMisconfiguredError(
        '交易服务未配置与登录中心相同的 JWT_SECRET（或配置中的 jwt_verify.secret）。请在 trading-engine 运行环境设置与 auth-service 一致的密钥后重试。'
      );
    }
    throw new Error(message);
  }
  return (await res.json()) as LoadMeResponse;
}

export async function listExchangeAccounts(): Promise<ExchangeAccountView[]> {
  const res = await apiFetch(manualTradingApiUrl('/exchange-accounts'));
  if (!res.ok) {
    throw new Error(await res.text());
  }
  return (await res.json()) as ExchangeAccountView[];
}

export async function saveExchangeAccount(body: SaveExchangeAccountBody): Promise<ExchangeAccountView> {
  const res = await apiFetch(manualTradingApiUrl('/exchange-accounts'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error(await res.text());
  }
  return (await res.json()) as ExchangeAccountView;
}

export async function listExchangeLeverageLimits(): Promise<ExchangeLeverageLimitView[]> {
  const res = await apiFetch(manualTradingApiUrl('/risk/exchange-leverage-limits'));
  if (!res.ok) {
    throw new Error(await parseApiError(res));
  }
  const raw = (await res.json()) as unknown;
  const arr = Array.isArray(raw) ? raw : [];
  return arr.map((item) => {
    const row = item as Record<string, unknown>;
    return {
      exchange: coerceRustNewtypeString(row.exchange),
      max_leverage: coerceApiNumber(row.max_leverage),
    };
  });
}

export async function upsertExchangeLeverageLimit(body: {
  exchange: string;
  max_leverage: number;
}): Promise<ExchangeLeverageLimitView> {
  const res = await apiFetch(manualTradingApiUrl('/risk/exchange-leverage-limits'), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error(await parseApiError(res));
  }
  const row = (await res.json()) as Record<string, unknown>;
  return {
    exchange: coerceRustNewtypeString(row.exchange),
    max_leverage: coerceApiNumber(row.max_leverage),
  };
}

export async function deleteExchangeLeverageLimit(exchange: string): Promise<void> {
  const enc = encodeURIComponent(exchange);
  const res = await apiFetch(manualTradingApiUrl(`/risk/exchange-leverage-limits/${enc}`), {
    method: 'DELETE',
  });
  if (!res.ok) {
    throw new Error(await parseApiError(res));
  }
}

export type ThresholdMode = 'Immediate' | 'GreaterOrEqual' | 'LessOrEqual';

/** 与 trading-engine `OpenInstructionRequest` 一致（开仓固定 one_shot）。 */
export type OpenInstructionBody = {
  symbol: string;
  long_inst_id: string;
  short_inst_id: string;
  long_exchange: string;
  short_exchange: string;
  target_notional: number;
  per_order_notional: number;
  threshold_mode: ThresholdMode;
  threshold?: number | null;
  min_order_interval_ms: number;
};

export async function createOpenInstruction(body: OpenInstructionBody): Promise<unknown> {
  const res = await apiFetch(manualTradingApiUrl('/instructions/open'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error(await res.text());
  }
  return res.json();
}

export type CloseInstructionBody = {
  symbol: string;
  long_inst_id: string;
  short_inst_id: string;
  long_exchange: string;
  short_exchange: string;
  /** one_shot：token 数量；与 target_notional 二选一，都有时以 size 为准。 */
  target_size?: number | null;
  /** one_shot：USD 名义；与 target_size 二选一。 */
  target_notional?: number | null;
  per_order_notional: number;
  threshold_mode: ThresholdMode;
  threshold?: number | null;
  min_order_interval_ms: number;
  validity?: 'one_shot' | 'persistent';
};

export async function createCloseInstruction(body: CloseInstructionBody): Promise<unknown> {
  const res = await apiFetch(manualTradingApiUrl('/instructions/close'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error(await res.text());
  }
  return res.json();
}

export async function cancelInstruction(instructionId: string): Promise<unknown> {
  const enc = encodeURIComponent(instructionId);
  const res = await apiFetch(manualTradingApiUrl(`/instructions/${enc}`), {
    method: 'DELETE',
  });
  if (!res.ok) {
    throw new Error(await res.text());
  }
  return res.json();
}

export async function pauseInstruction(instructionId: string): Promise<InstructionView> {
  const enc = encodeURIComponent(instructionId);
  const res = await apiFetch(manualTradingApiUrl(`/instructions/${enc}/pause`), {
    method: 'POST',
  });
  if (!res.ok) {
    throw new Error(await parseApiError(res));
  }
  return (await res.json()) as InstructionView;
}

export async function resumeInstruction(instructionId: string): Promise<InstructionView> {
  const enc = encodeURIComponent(instructionId);
  const res = await apiFetch(manualTradingApiUrl(`/instructions/${enc}/resume`), {
    method: 'POST',
  });
  if (!res.ok) {
    throw new Error(await parseApiError(res));
  }
  return (await res.json()) as InstructionView;
}

export type GlobalOpenPauseView = {
  paused: boolean;
};

export async function getGlobalOpenPause(): Promise<GlobalOpenPauseView> {
  const res = await apiFetch(manualTradingApiUrl('/instructions/global-open-pause'));
  if (!res.ok) {
    throw new Error(await parseApiError(res));
  }
  return (await res.json()) as GlobalOpenPauseView;
}

export async function pauseGlobalOpen(): Promise<GlobalOpenPauseView> {
  const res = await apiFetch(manualTradingApiUrl('/instructions/global-open-pause/pause'), {
    method: 'POST',
  });
  if (!res.ok) {
    throw new Error(await parseApiError(res));
  }
  return (await res.json()) as GlobalOpenPauseView;
}

export async function resumeGlobalOpen(): Promise<GlobalOpenPauseView> {
  const res = await apiFetch(manualTradingApiUrl('/instructions/global-open-pause/resume'), {
    method: 'POST',
  });
  if (!res.ok) {
    throw new Error(await parseApiError(res));
  }
  return (await res.json()) as GlobalOpenPauseView;
}

/** PATCH /instructions/:instruction_id → `UpdateInstructionRequest` */
export type UpdateInstructionBody = {
  target_notional?: number;
  per_order_notional?: number;
  threshold_mode?: ThresholdMode;
  threshold?: number | null;
  min_order_interval_ms?: number;
};

export async function updateInstruction(
  instructionId: string,
  body: UpdateInstructionBody
): Promise<InstructionView> {
  const enc = encodeURIComponent(instructionId);
  const res = await apiFetch(manualTradingApiUrl(`/instructions/${enc}`), {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error(await res.text());
  }
  return (await res.json()) as InstructionView;
}

/** 与 trading-engine `InstructionView` 一致。 */
export type InstructionValidityView = 'one_shot' | 'persistent';

export type InstructionView = {
  instruction_id: string;
  user_id: string;
  symbol: string;
  action: 'Open' | 'Close';
  long_inst_id: string;
  short_inst_id: string;
  long_exchange: string;
  short_exchange: string;
  target_notional: number;
  per_order_notional: number;
  executed_notional: number;
  /** One-shot close: token size snapshot at instruction creation. */
  target_size: number;
  /** One-shot close: cumulative token size submitted for closing. */
  executed_size: number;
  validity: InstructionValidityView;
  threshold_mode: ThresholdMode;
  threshold: number | null;
  min_order_interval_ms: number;
  status: string;
};

export type SymbolPairFilterParams = {
  symbol: string;
  long_exchange: string;
  short_exchange: string;
  limit?: number;
};

export type ListInstructionsParams = {
  symbol?: string;
  exchange1?: string;
  exchange2?: string;
  action?: 'Open' | 'Close';
  include_history?: boolean;
};

export type HistoryOrdersFilterParams = {
  symbol?: string;
  long_exchange?: string;
  short_exchange?: string;
  task_id?: string;
  instruction_id?: string;
  position_group_id?: string;
  limit?: number;
};

export type HistoryTasksFilterParams = HistoryOrdersFilterParams;

function historyFilterQueryString(params: HistoryOrdersFilterParams): string {
  const q = new URLSearchParams();
  if (params.symbol?.trim()) {
    q.set('symbol', params.symbol.trim());
  }
  if (params.long_exchange) {
    q.set('long_exchange', params.long_exchange);
  }
  if (params.short_exchange) {
    q.set('short_exchange', params.short_exchange);
  }
  if (params.limit != null) {
    q.set('limit', String(params.limit));
  }
  if (params.task_id?.trim()) {
    q.set('task_id', params.task_id.trim());
  }
  if (params.instruction_id?.trim()) {
    q.set('instruction_id', params.instruction_id.trim());
  }
  if (params.position_group_id?.trim()) {
    q.set('position_group_id', params.position_group_id.trim());
  }
  return q.toString();
}

function listInstructionsQueryString(params: ListInstructionsParams): string {
  const q = new URLSearchParams();
  const symbol = params.symbol?.trim();
  if (symbol) {
    q.set('symbol', symbol);
  }
  if (params.exchange1) {
    q.set('exchange1', params.exchange1);
  }
  if (params.exchange2) {
    q.set('exchange2', params.exchange2);
  }
  if (params.action) {
    q.set('action', params.action);
  }
  if (params.include_history) {
    q.set('include_history', 'true');
  }
  return q.toString();
}

const listInstructionsInflight = new Map<string, Promise<InstructionView[]>>();


export function isListInstructionsInFlight(params: ListInstructionsParams = {}): boolean {
  const cacheKey = listInstructionsQueryString(params) || '__all__';
  return listInstructionsInflight.has(cacheKey);
}

async function fetchListInstructions(params: ListInstructionsParams): Promise<InstructionView[]> {
  const query = listInstructionsQueryString(params);
  const suffix = query ? `?${query}` : '';
  const res = await apiFetch(manualTradingApiUrl(`/instructions${suffix}`));
  if (!res.ok) {
    throw new Error(await res.text());
  }
  return (await res.json()) as InstructionView[];
}

export async function listInstructions(params: ListInstructionsParams = {}): Promise<InstructionView[]> {
  const cacheKey = listInstructionsQueryString(params) || '__all__';
  const inflight = listInstructionsInflight.get(cacheKey);
  if (inflight) {
    return inflight;
  }

  const promise = fetchListInstructions(params).finally(() => {
    listInstructionsInflight.delete(cacheKey);
  });

  listInstructionsInflight.set(cacheKey, promise);
  return promise;
}

/** Poll helper: returns `null` instead of stacking another HTTP call for the same query. */
export async function listInstructionsIfIdle(
  params: ListInstructionsParams = {},
): Promise<InstructionView[] | null> {
  const cacheKey = listInstructionsQueryString(params) || '__all__';
  if (listInstructionsInflight.has(cacheKey)) {
    return null;
  }
  return listInstructions(params);
}

/** GET /tasks/in-flight | /tasks/history → `TaskView` */
export type InFlightLegView = {
  exchange: string;
  symbol: string;
  inst_id: string;
  client_order_id: string;
  position_side: string;
  order_type: string;
  /** 提交价（commit price） */
  price: number | null;
  /** Decimal 在后端可能序列化为字符串 */
  quantity: string | number;
  status: string;
  registered_at_ms: number;
  submitted_at_ms: number | null;
  filled_at_ms: number | null;
  filled_price: number | null;
  /** 相对提交价的滑点比例，如 0.001 = 0.1% */
  slippage_ratio: number | null;
  /** 从注册到提交/成交的耗时（毫秒），历史归档任务主要展示此字段 */
  execution_time_cost_ms: number | null;
};

export type TaskView = {
  task_id: string;
  user_id: string;
  symbol: string;
  action: 'Open' | 'Close';
  created_at: number;
  long_leg: InFlightLegView;
  short_leg: InFlightLegView;
  status: string;
  updated_at: number;
  /** 提交时对冲价差 (short-long)/middle */
  commit_spread_ratio: number | null;
  /** 成交时对冲价差 (short-long)/middle */
  fill_spread_ratio: number | null;
};

/** @deprecated 使用 TaskView */
export type InFlightTaskView = TaskView;

export async function listInFlightTasks(): Promise<TaskView[]> {
  const res = await apiFetch(manualTradingApiUrl('/tasks/in-flight'));
  if (!res.ok) {
    throw new Error(await res.text());
  }
  const rows = (await res.json()) as TaskView[];
  return normalizeTaskRows(rows);
}

/** DELETE /tasks/in-flight → `RemoveInFlightTaskRequest` */
export type RemoveInFlightTaskBody = {
  long_exchange: string;
  long_inst_id: string;
  short_exchange: string;
  short_inst_id: string;
};

export function taskKeyBodyFromTaskView(row: TaskView): RemoveInFlightTaskBody {
  const legInstId = (leg: InFlightLegView) => (leg.status === 'Skipped' ? '' : leg.inst_id);
  return {
    long_exchange: row.long_leg.exchange,
    long_inst_id: legInstId(row.long_leg),
    short_exchange: row.short_leg.exchange,
    short_inst_id: legInstId(row.short_leg),
  };
}

export async function removeInFlightTask(body: RemoveInFlightTaskBody): Promise<TaskView> {
  const res = await apiFetch(manualTradingApiUrl('/tasks/in-flight'), {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error(await res.text());
  }
  const row = (await res.json()) as TaskView;
  return normalizeTaskRows([row])[0];
}

/** GET /tasks/history → 双腿均已终态的历史任务 */
export async function listHistoryTasks(params: HistoryTasksFilterParams): Promise<TaskView[]> {
  const res = await apiFetch(
    manualTradingApiUrl(`/tasks/history?${historyFilterQueryString(params)}`),
  );
  if (!res.ok) {
    throw new Error(await res.text());
  }
  const rows = (await res.json()) as TaskView[];
  return normalizeTaskRows(rows);
}

/** GET /orders/history → 历史订单明细 */
export type HistoryOrderView = {
  task_id: string;
  instruction_id?: string | null;
  position_group_id?: string | null;
  symbol: string;
  task_action: 'Open' | 'Close';
  client_order_id: string;
  exchange: string;
  inst_id: string;
  action: 'Open' | 'Close';
  position_side: string;
  status: string;
  token_size: number;
  notional_usd: number;
  commit_price: number;
  commit_time_ms: number;
  filled_price: number | null;
  filled_time_ms: number | null;
  fill_duration_ms: number | null;
  slippage_ratio: number | null;
  updated_at_ms: number;
};

export async function listHistoryOrders(
  params: HistoryOrdersFilterParams,
): Promise<HistoryOrderView[]> {
  const res = await apiFetch(
    manualTradingApiUrl(`/orders/history?${historyFilterQueryString(params)}`),
  );
  if (!res.ok) {
    throw new Error(await res.text());
  }
  return (await res.json()) as HistoryOrderView[];
}

function coerceOptionalApiNumber(v: unknown): number | null {
  if (v == null) return null;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const n = Number(v.trim());
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function normalizeTaskRows(rows: TaskView[]): TaskView[] {
  return rows.map((row) => ({
    ...row,
    task_id: coerceRustNewtypeString(row.task_id),
    user_id: coerceRustNewtypeString(row.user_id),
    status: coerceRustNewtypeString(row.status),
    updated_at: Number(row.updated_at) || 0,
    commit_spread_ratio: coerceOptionalApiNumber(row.commit_spread_ratio),
    fill_spread_ratio: coerceOptionalApiNumber(row.fill_spread_ratio),
    long_leg: normalizeTaskLeg(row.long_leg),
    short_leg: normalizeTaskLeg(row.short_leg),
  }));
}

function normalizeTaskLeg(leg: InFlightLegView): InFlightLegView {
  return {
    ...leg,
    client_order_id: coerceRustNewtypeString(leg.client_order_id),
    exchange: coerceRustNewtypeString(leg.exchange),
    price: coerceOptionalApiNumber(leg.price),
    filled_price: coerceOptionalApiNumber(leg.filled_price),
    slippage_ratio: coerceOptionalApiNumber(leg.slippage_ratio),
    execution_time_cost_ms:
      leg.execution_time_cost_ms == null
        ? null
        : Number(leg.execution_time_cost_ms) || null,
    submitted_at_ms:
      leg.submitted_at_ms == null ? null : Number(leg.submitted_at_ms) || null,
    filled_at_ms: leg.filled_at_ms == null ? null : Number(leg.filled_at_ms) || null,
  };
}

/** Rust `struct Id(String)` 无 `serde(transparent)` 时常序列化为 `["uuid"]`，兼容纯字符串 */
function coerceRustNewtypeString(v: unknown): string {
  if (typeof v === 'string') return v;
  if (Array.isArray(v) && v.length > 0) {
    const x = v[0];
    if (typeof x === 'string') return x;
    if (typeof x === 'number' || typeof x === 'boolean') return String(x);
  }
  if (v == null) return '';
  return String(v);
}

function coerceApiNumber(v: unknown, fallback = 0): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const n = Number(v.trim());
    if (Number.isFinite(n)) return n;
  }
  return fallback;
}

/** `domain::FundingRecord` */
export type FundingRecordView = {
  ts: number;
  rate: number;
  notional_usd: number;
  fee_value: number;
};

/** GET /position-groups → `domain::Position`（quantity/size 常为 JSON 字符串） */
export type PositionInGroupView = {
  exchange: string;
  symbol: string;
  inst_id: string;
  side: string;
  quantity: number;
  size: number;
  imr: number;
  usd_value: number;
  average_price: number;
  mark_price: number;
  orderbook_price: number;
  liq_price: number;
  unrealized_pnl: number;
  upl_ratio: number;
  realized_pnl: number;
  leverage: number;
  fee: number;
  funding_fee: number;
  funding_history: FundingRecordView[];
  last_update_time: number;
  created_time: number;
  adl_indicator: number;
  /** 资金费结算周期（分钟） */
  funding_interval: number | null;
  next_funding_time_ms: number | null;
  next_funding_fee: number | null;
  next_funding_fee_ratio: number | null;
};

/** GET /position-groups → `domain::PositionGroup` */
export type PositionGroupView = {
  group_id: string;
  arbitrage_type: string;
  long_position: PositionInGroupView | null;
  short_position: PositionInGroupView | null;
  funding_fee_net: number;
  estimated_next_funding_fee_net: number | null;
  next_funding_time_ms: number | null;
  avg_open_spread: number | null;
  current_orderbook_spread: number | null;
  target_close_spread: number | null;
  last_open_position_time: number | null;
  last_close_position_time: number | null;
};

function mapFundingRecord(raw: unknown): FundingRecordView | null {
  if (raw == null || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  return {
    ts: Number(r.ts) || 0,
    rate: coerceApiNumber(r.rate),
    notional_usd: coerceApiNumber(r.notional_usd),
    fee_value: coerceApiNumber(r.fee_value),
  };
}

function mapPositionInGroup(raw: unknown): PositionInGroupView | null {
  if (raw == null || typeof raw !== 'object') return null;
  const p = raw as Record<string, unknown>;
  const fundingHistoryRaw = Array.isArray(p.funding_history) ? p.funding_history : [];
  return {
    exchange: coerceRustNewtypeString(p.exchange),
    symbol: typeof p.symbol === 'string' ? p.symbol : String(p.symbol ?? ''),
    inst_id: typeof p.inst_id === 'string' ? p.inst_id : String(p.inst_id ?? ''),
    side: typeof p.side === 'string' ? p.side : String(p.side ?? ''),
    quantity: coerceApiNumber(p.quantity),
    size: coerceApiNumber(p.size),
    imr: coerceApiNumber(p.imr),
    usd_value: coerceApiNumber(p.usd_value),
    average_price: coerceApiNumber(p.average_price),
    mark_price: coerceApiNumber(p.mark_price),
    orderbook_price: coerceApiNumber(p.orderbook_price),
    liq_price: coerceApiNumber(p.liq_price),
    unrealized_pnl: coerceApiNumber(p.unrealized_pnl),
    upl_ratio: coerceApiNumber(p.upl_ratio),
    realized_pnl: coerceApiNumber(p.realized_pnl),
    leverage: coerceApiNumber(p.leverage),
    fee: coerceApiNumber(p.fee),
    funding_fee: coerceApiNumber(p.funding_fee),
    funding_history: fundingHistoryRaw
      .map((item) => mapFundingRecord(item))
      .filter((item): item is FundingRecordView => item != null),
    last_update_time: Number(p.last_update_time) || 0,
    created_time: Number(p.created_time) || 0,
    adl_indicator: Number(p.adl_indicator) || 0,
    funding_interval:
      p.funding_interval == null ? null : Number(p.funding_interval) || null,
    next_funding_time_ms:
      p.next_funding_time_ms == null ? null : Number(p.next_funding_time_ms) || null,
    next_funding_fee:
      p.next_funding_fee == null ? null : coerceOptionalApiNumber(p.next_funding_fee),
    next_funding_fee_ratio:
      p.next_funding_fee_ratio == null ? null : coerceOptionalApiNumber(p.next_funding_fee_ratio),
  };
}

function mapPositionGroupFromRaw(item: unknown): PositionGroupView {
  const g = item as Record<string, unknown>;
  const coerceOptionalSpread = (v: unknown): number | null =>
    v == null ? null : coerceApiNumber(v);
  return {
    group_id: coerceRustNewtypeString(g.group_id),
    arbitrage_type:
      typeof g.arbitrage_type === 'string'
        ? g.arbitrage_type
        : String(g.arbitrage_type ?? ''),
    long_position: mapPositionInGroup(g.long_position),
    short_position: mapPositionInGroup(g.short_position),
    funding_fee_net: coerceApiNumber(g.funding_fee_net),
    estimated_next_funding_fee_net:
      g.estimated_next_funding_fee_net == null
        ? null
        : coerceApiNumber(g.estimated_next_funding_fee_net),
    next_funding_time_ms:
      g.next_funding_time_ms == null ? null : Number(g.next_funding_time_ms) || null,
    avg_open_spread: coerceOptionalSpread(g.avg_open_spread),
    current_orderbook_spread: coerceOptionalSpread(g.current_orderbook_spread),
    target_close_spread: coerceOptionalSpread(g.target_close_spread),
    last_open_position_time:
      g.last_open_position_time == null ? null : Number(g.last_open_position_time) || null,
    last_close_position_time:
      g.last_close_position_time == null ? null : Number(g.last_close_position_time) || null,
  };
}

export async function listPositionGroups(): Promise<PositionGroupView[]> {
  const res = await apiFetch(manualTradingApiUrl('/position-groups'));
  if (!res.ok) {
    throw new Error(await res.text());
  }
  const raw = (await res.json()) as unknown;
  const arr = Array.isArray(raw) ? raw : [];
  return arr.map(mapPositionGroupFromRaw);
}

export type PositionGroupSettingView = {
  group_id: string;
  auto_balance_enabled: boolean;
};

function mapPositionGroupSetting(raw: unknown): PositionGroupSettingView | null {
  if (raw == null || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  const groupId = coerceRustNewtypeString(row.group_id);
  if (!groupId) return null;
  const enabledRaw = row.auto_balance_enabled ?? row.enabled;
  return {
    group_id: groupId,
    auto_balance_enabled: enabledRaw !== false && enabledRaw !== 0 && enabledRaw !== '0',
  };
}

/** GET /position-groups/settings */
export async function listPositionGroupSettings(): Promise<PositionGroupSettingView[]> {
  const res = await apiFetch(manualTradingApiUrl('/position-groups/settings'));
  if (!res.ok) {
    throw new Error(await parseApiError(res));
  }
  const raw = (await res.json()) as { settings?: unknown[] };
  const arr = Array.isArray(raw?.settings) ? raw.settings : [];
  return arr
    .map((item) => mapPositionGroupSetting(item))
    .filter((item): item is PositionGroupSettingView => item != null);
}

/** PUT /position-groups/:group_id/settings */
export async function updatePositionGroupSetting(
  groupId: string,
  autoBalanceEnabled: boolean,
): Promise<PositionGroupSettingView> {
  const res = await apiFetch(
    manualTradingApiUrl(`/position-groups/${encodeURIComponent(groupId)}/settings`),
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ auto_balance_enabled: autoBalanceEnabled }),
    },
  );
  if (!res.ok) {
    throw new Error(await parseApiError(res));
  }
  const mapped = mapPositionGroupSetting(await res.json());
  if (!mapped) {
    throw new Error('invalid position group setting response');
  }
  return mapped;
}

export type ListPositionGroupHistoryParams = {
  symbol?: string;
  created_from_ms?: number;
  created_to_ms?: number;
};

export type PositionGroupHistoryView = {
  group_id: string;
  symbol: string;
  long_exchange: string;
  short_exchange: string;
  long_inst_id: string;
  short_inst_id: string;
  closed_token_size: number;
  closed_quantity: number;
  closed_notional_usd: number;
  close_leg_count: number;
  total_pnl: number;
  spread_pnl: number;
  funding_fee_net: number;
  trading_fee: number;
  close_started_at_ms: number;
  completed_at_ms: number;
};

export type PositionGroupHistorySummaryView = {
  count: number;
  total_pnl: number;
  spread_pnl: number;
  funding_fee_net: number;
  trading_fee: number;
};

export type PositionGroupHistoryListView = {
  total: number;
  summary: PositionGroupHistorySummaryView;
  items: PositionGroupHistoryView[];
};

function mapPositionGroupHistoryFromRaw(item: unknown): PositionGroupHistoryView {
  const row = item as Record<string, unknown>;
  return {
    group_id: coerceRustNewtypeString(row.group_id),
    symbol: typeof row.symbol === 'string' ? row.symbol : String(row.symbol ?? ''),
    long_exchange: coerceRustNewtypeString(row.long_exchange),
    short_exchange: coerceRustNewtypeString(row.short_exchange),
    long_inst_id: typeof row.long_inst_id === 'string' ? row.long_inst_id : String(row.long_inst_id ?? ''),
    short_inst_id:
      typeof row.short_inst_id === 'string' ? row.short_inst_id : String(row.short_inst_id ?? ''),
    closed_token_size: coerceApiNumber(row.closed_token_size),
    closed_quantity: coerceApiNumber(row.closed_quantity),
    closed_notional_usd: coerceApiNumber(row.closed_notional_usd),
    close_leg_count: Number(row.close_leg_count) || 0,
    total_pnl: coerceApiNumber(row.total_pnl),
    spread_pnl: coerceApiNumber(row.spread_pnl),
    funding_fee_net: coerceApiNumber(row.funding_fee_net),
    trading_fee: coerceApiNumber(row.trading_fee),
    close_started_at_ms: Number(row.close_started_at_ms) || 0,
    completed_at_ms: Number(row.completed_at_ms) || 0,
  };
}

export async function listPositionGroupHistory(
  params: ListPositionGroupHistoryParams = {},
): Promise<PositionGroupHistoryListView> {
  const q = new URLSearchParams();
  if (params.symbol?.trim()) q.set('symbol', params.symbol.trim());
  if (params.created_from_ms != null) q.set('created_from_ms', String(params.created_from_ms));
  if (params.created_to_ms != null) q.set('created_to_ms', String(params.created_to_ms));

  const suffix = q.toString() ? `?${q.toString()}` : '';
  const res = await apiFetch(manualTradingApiUrl(`/position-groups/history${suffix}`));
  if (!res.ok) {
    throw new Error(await res.text());
  }
  const raw = (await res.json()) as Record<string, unknown>;
  const itemsRaw = Array.isArray(raw.items) ? raw.items : [];
  const summaryRaw = (raw.summary ?? {}) as Record<string, unknown>;
  return {
    total: Number(raw.total) || 0,
    summary: {
      count: Number(summaryRaw.count) || 0,
      total_pnl: coerceApiNumber(summaryRaw.total_pnl),
      spread_pnl: coerceApiNumber(summaryRaw.spread_pnl),
      funding_fee_net: coerceApiNumber(summaryRaw.funding_fee_net),
      trading_fee: coerceApiNumber(summaryRaw.trading_fee),
    },
    items: itemsRaw.map(mapPositionGroupHistoryFromRaw),
  };
}

/** POST /tasks/single-leg → 直接提交单腿 ExecutionTask */
export type SubmitSingleLegTaskBody = {
  exchange: string;
  symbol: string;
  inst_id: string;
  action: 'Open' | 'Close';
  position_side: 'Long' | 'Short';
  /** USD notional; converted server-side when token_size is omitted */
  notional?: number;
  /** Base token quantity (not contract/order quantity) */
  token_size?: number;
  price?: number;
};

export type SubmitSingleLegTaskResponse = {
  task_id: string;
};

export async function submitSingleLegTask(
  body: SubmitSingleLegTaskBody
): Promise<SubmitSingleLegTaskResponse> {
  const res = await apiFetch(manualTradingApiUrl('/tasks/single-leg'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error(await res.text());
  }
  return (await res.json()) as SubmitSingleLegTaskResponse;
}

/** GET /synced-positions → 各交易所 REST 同步的原始仓位 */
export type SyncedPositionsView = {
  exchange: string;
  synced_at_ms: number;
  positions: PositionInGroupView[];
};

function mapSyncedPositionsFromRaw(item: unknown): SyncedPositionsView {
  const row = item as Record<string, unknown>;
  const positionsRaw = Array.isArray(row.positions) ? row.positions : [];
  return {
    exchange: coerceRustNewtypeString(row.exchange),
    synced_at_ms: Number(row.synced_at_ms) || 0,
    positions: positionsRaw
      .map((p) => mapPositionInGroup(p))
      .filter((p): p is PositionInGroupView => p != null),
  };
}

export async function listSyncedPositions(): Promise<SyncedPositionsView[]> {
  const res = await apiFetch(manualTradingApiUrl('/synced-positions'));
  if (!res.ok) {
    throw new Error(await res.text());
  }
  const raw = (await res.json()) as unknown;
  const arr = Array.isArray(raw) ? raw : [];
  return arr.map(mapSyncedPositionsFromRaw);
}

/** GET /accounts → runtime 缓存的各交易所账户权益 */
export type AssetBalanceView = {
  asset: string;
  usd_value: number;
  balance: number;
  equity: number;
};

export type AccountInfoView = {
  user_id: string;
  exchange: string;
  total_usd: number;
  im_rate: number;
  mm_rate: number;
  assets: AssetBalanceView[];
};

function mapAssetBalance(raw: unknown): AssetBalanceView {
  const a = raw as Record<string, unknown>;
  return {
    asset: typeof a.asset === 'string' ? a.asset : String(a.asset ?? ''),
    usd_value: coerceApiNumber(a.usd_value),
    balance: coerceApiNumber(a.balance),
    equity: coerceApiNumber(a.equity),
  };
}

export function mapAccountInfo(raw: unknown): AccountInfoView {
  const a = raw as Record<string, unknown>;
  const assetsRaw = Array.isArray(a.assets) ? a.assets : [];
  return {
    user_id: coerceRustNewtypeString(a.user_id),
    exchange: coerceRustNewtypeString(a.exchange),
    total_usd: coerceApiNumber(a.total_usd),
    im_rate: coerceApiNumber(a.im_rate),
    mm_rate: coerceApiNumber(a.mm_rate),
    assets: assetsRaw.map(mapAssetBalance),
  };
}

export async function listAccounts(): Promise<AccountInfoView[]> {
  const res = await apiFetch(manualTradingApiUrl('/accounts'));
  if (!res.ok) {
    throw new Error(await res.text());
  }
  const raw = (await res.json()) as unknown;
  const arr = Array.isArray(raw) ? raw : [];
  return arr.map(mapAccountInfo);
}

/** POST /accounts/sync — 从交易所拉取并返回最新账户信息 */
export async function syncAccounts(): Promise<AccountInfoView[]> {
  const res = await apiFetch(manualTradingApiUrl('/accounts/sync'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
  if (!res.ok) {
    throw new Error(await res.text());
  }
  const raw = (await res.json()) as unknown;
  const arr = Array.isArray(raw) ? raw : [];
  return arr.map(mapAccountInfo);
}

/** GET /accounts/daily-pnl → 当日账户权益变动（首条快照为 UTC 日起始基准） */
export type AccountUsdView = {
  exchange: string;
  total_usd: number;
};

export type AccountHistorySnapshotView = {
  recorded_at_ms: number;
  total_usd: number;
  accounts: AccountUsdView[];
};

export type DailyAccountPnlView = {
  day_start_ms: number;
  baseline_total_usd: number | null;
  current_total_usd: number;
  daily_pnl: number | null;
  daily_pnl_ratio: number | null;
  snapshots: AccountHistorySnapshotView[];
};

export function mapDailyAccountPnl(raw: unknown): DailyAccountPnlView {
  const d = raw as Record<string, unknown>;
  const snapshotsRaw = Array.isArray(d.snapshots) ? d.snapshots : [];
  return {
    day_start_ms: Number(d.day_start_ms) || 0,
    baseline_total_usd:
      d.baseline_total_usd == null ? null : coerceOptionalApiNumber(d.baseline_total_usd),
    current_total_usd: coerceApiNumber(d.current_total_usd),
    daily_pnl: d.daily_pnl == null ? null : coerceOptionalApiNumber(d.daily_pnl),
    daily_pnl_ratio:
      d.daily_pnl_ratio == null ? null : coerceOptionalApiNumber(d.daily_pnl_ratio),
    snapshots: snapshotsRaw.map((item) => {
      const s = item as Record<string, unknown>;
      const accountsRaw = Array.isArray(s.accounts) ? s.accounts : [];
      return {
        recorded_at_ms: Number(s.recorded_at_ms) || 0,
        total_usd: coerceApiNumber(s.total_usd),
        accounts: accountsRaw.map((row) => {
          const a = row as Record<string, unknown>;
          return {
            exchange: coerceRustNewtypeString(a.exchange),
            total_usd: coerceApiNumber(a.total_usd),
          };
        }),
      };
    }),
  };
}

export async function getDailyAccountPnl(dayStartMs?: number): Promise<DailyAccountPnlView> {
  const q = new URLSearchParams();
  if (dayStartMs != null) {
    q.set('day_start_ms', String(dayStartMs));
  }
  const suffix = q.toString() ? `?${q.toString()}` : '';
  const res = await apiFetch(manualTradingApiUrl(`/accounts/daily-pnl${suffix}`));
  if (!res.ok) {
    throw new Error(await res.text());
  }
  return mapDailyAccountPnl(await res.json());
}

export type ExchangeAccountSummaryView = {
  exchange: string;
  total_usd: number;
  im_rate: number;
  mm_rate: number;
  share_ratio: number;
  last_sync_at_ms: number | null;
};

export type AccountSummaryView = {
  total_usd: number;
  exchange_count: number;
  max_im_rate: number;
  max_mm_rate: number;
  last_sync_at_ms: number | null;
  daily_pnl: number | null;
  daily_pnl_ratio: number | null;
  exchanges: ExchangeAccountSummaryView[];
};

export function mapAccountSummary(raw: unknown): AccountSummaryView {
  const d = raw as Record<string, unknown>;
  const exchangesRaw = Array.isArray(d.exchanges) ? d.exchanges : [];
  return {
    total_usd: coerceApiNumber(d.total_usd),
    exchange_count: Number(d.exchange_count) || 0,
    max_im_rate: coerceApiNumber(d.max_im_rate),
    max_mm_rate: coerceApiNumber(d.max_mm_rate),
    last_sync_at_ms:
      d.last_sync_at_ms == null ? null : Number(d.last_sync_at_ms) || null,
    daily_pnl: d.daily_pnl == null ? null : coerceOptionalApiNumber(d.daily_pnl),
    daily_pnl_ratio:
      d.daily_pnl_ratio == null ? null : coerceOptionalApiNumber(d.daily_pnl_ratio),
    exchanges: exchangesRaw.map((item) => {
      const row = item as Record<string, unknown>;
      return {
        exchange: coerceRustNewtypeString(row.exchange),
        total_usd: coerceApiNumber(row.total_usd),
        im_rate: coerceApiNumber(row.im_rate),
        mm_rate: coerceApiNumber(row.mm_rate),
        share_ratio: coerceApiNumber(row.share_ratio),
        last_sync_at_ms:
          row.last_sync_at_ms == null ? null : Number(row.last_sync_at_ms) || null,
      };
    }),
  };
}

export async function getAccountSummary(): Promise<AccountSummaryView> {
  const res = await apiFetch(manualTradingApiUrl('/accounts/summary'));
  if (!res.ok) {
    throw new Error(await res.text());
  }
  return mapAccountSummary(await res.json());
}

export type AccountDailySummaryView = {
  day_start_ms: number;
  open_usd: number;
  close_usd: number;
  high_usd: number;
  low_usd: number;
  daily_pnl: number;
  daily_pnl_ratio: number | null;
  snapshot_count: number;
  close_accounts: AccountUsdView[];
};

export type AccountDailySummaryListView = {
  from_ms: number;
  to_ms: number;
  days: AccountDailySummaryView[];
};

export function mapAccountDailySummaries(raw: unknown): AccountDailySummaryListView {
  const d = raw as Record<string, unknown>;
  const daysRaw = Array.isArray(d.days) ? d.days : [];
  return {
    from_ms: Number(d.from_ms) || 0,
    to_ms: Number(d.to_ms) || 0,
    days: daysRaw.map((item) => {
      const row = item as Record<string, unknown>;
      const accountsRaw = Array.isArray(row.close_accounts) ? row.close_accounts : [];
      return {
        day_start_ms: Number(row.day_start_ms) || 0,
        open_usd: coerceApiNumber(row.open_usd),
        close_usd: coerceApiNumber(row.close_usd),
        high_usd: coerceApiNumber(row.high_usd),
        low_usd: coerceApiNumber(row.low_usd),
        daily_pnl: coerceApiNumber(row.daily_pnl),
        daily_pnl_ratio:
          row.daily_pnl_ratio == null ? null : coerceOptionalApiNumber(row.daily_pnl_ratio),
        snapshot_count: Number(row.snapshot_count) || 0,
        close_accounts: accountsRaw.map((acc) => {
          const a = acc as Record<string, unknown>;
          return {
            exchange: coerceRustNewtypeString(a.exchange),
            total_usd: coerceApiNumber(a.total_usd),
          };
        }),
      };
    }),
  };
}

export async function getAccountDailySummaries(params?: {
  fromMs?: number;
  toMs?: number;
}): Promise<AccountDailySummaryListView> {
  const q = new URLSearchParams();
  if (params?.fromMs != null) q.set('from_ms', String(params.fromMs));
  if (params?.toMs != null) q.set('to_ms', String(params.toMs));
  const suffix = q.toString() ? `?${q.toString()}` : '';
  const res = await apiFetch(manualTradingApiUrl(`/accounts/daily-summaries${suffix}`));
  if (!res.ok) {
    throw new Error(await res.text());
  }
  return mapAccountDailySummaries(await res.json());
}

/** POST /positions/sync — 触发交易所持仓同步，无响应体。 */
export async function syncPositions(): Promise<void> {
  const res = await apiFetch(manualTradingApiUrl('/positions/sync'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
  if (!res.ok) {
    throw new Error(await res.text());
  }
}

export function mapPortfolioSnapshot(raw: unknown): PortfolioSnapshotData | null {
  if (raw == null || typeof raw !== 'object') return null;
  const payload = raw as Record<string, unknown>;
  if (payload.type !== 'snapshot') return null;
  const groupsRaw = Array.isArray(payload.position_groups) ? payload.position_groups : [];
  const syncedRaw = Array.isArray(payload.synced_positions) ? payload.synced_positions : [];
  const accountsRaw = Array.isArray(payload.accounts) ? payload.accounts : [];
  return {
    ts_ms: Number(payload.ts_ms) || Date.now(),
    position_groups: groupsRaw.map(mapPositionGroupFromRaw),
    synced_positions: syncedRaw.map(mapSyncedPositionsFromRaw),
    accounts: accountsRaw.map(mapAccountInfo),
    daily_pnl: mapDailyAccountPnl(payload.daily_pnl ?? {}),
  };
}

export type PortfolioSnapshotData = {
  ts_ms: number;
  position_groups: PositionGroupView[];
  synced_positions: SyncedPositionsView[];
  accounts: AccountInfoView[];
  daily_pnl: DailyAccountPnlView;
};

export function isManualTradingConfigured(): boolean {
  return hasManualTradingOrigin();
}

// ── Fund balances / transfers ─────────────────────────────────────────────

export type ExchangeBalanceView = {
  exchange: string;
  balance_usd: number;
  usdt_trading: number;
  usdt_funding: number;
};

export type FundPortfolioView = {
  okx: ExchangeBalanceView;
  bybit: ExchangeBalanceView;
  diff_ratio: number;
  total_usd: number;
};

export type FundPortfolioResponse = {
  portfolio: FundPortfolioView;
};

export type TransferTriggerType = 'manual' | 'auto';

export type TransferRecordView = {
  id: number;
  plan_id: string;
  trigger_type: TransferTriggerType;
  status: string;
  source_exchange: string;
  target_exchange: string;
  transfer_currency: string;
  planned_amount: number;
  actual_amount: number | null;
  error_message: string | null;
  started_at: string;
  completed_at: string | null;
};

export type TransferActionResponse = {
  plan_id: string;
};

export type AutoBalanceConfigView = {
  enabled: boolean;
  threshold_ratio: number;
  poll_interval_secs: number;
  cooldown_secs: number;
  transfer_currency: string;
  min_transfer_amount: number;
  max_transfer_amount: number;
};

export type AutoBalanceStatusResponse = {
  config: AutoBalanceConfigView;
  portfolio: FundPortfolioView | null;
  has_in_flight_transfer: boolean;
};

function mapExchangeBalanceView(raw: unknown): ExchangeBalanceView {
  const r = raw as Record<string, unknown>;
  return {
    exchange: coerceRustNewtypeString(r.exchange),
    balance_usd: coerceApiNumber(r.balance_usd),
    usdt_trading: coerceApiNumber(r.usdt_trading),
    usdt_funding: coerceApiNumber(r.usdt_funding),
  };
}

function mapFundPortfolioView(raw: unknown): FundPortfolioView {
  const r = raw as Record<string, unknown>;
  return {
    okx: mapExchangeBalanceView(r.okx),
    bybit: mapExchangeBalanceView(r.bybit),
    diff_ratio: coerceApiNumber(r.diff_ratio),
    total_usd: coerceApiNumber(r.total_usd),
  };
}

function mapTransferRecordView(raw: unknown): TransferRecordView {
  const r = raw as Record<string, unknown>;
  return {
    id: Number(r.id) || 0,
    plan_id: typeof r.plan_id === 'string' ? r.plan_id : String(r.plan_id ?? ''),
    trigger_type: r.trigger_type === 'auto' ? 'auto' : 'manual',
    status: typeof r.status === 'string' ? r.status : String(r.status ?? ''),
    source_exchange: coerceRustNewtypeString(r.source_exchange),
    target_exchange: coerceRustNewtypeString(r.target_exchange),
    transfer_currency: typeof r.transfer_currency === 'string' ? r.transfer_currency : 'USDT',
    planned_amount: coerceApiNumber(r.planned_amount),
    actual_amount: r.actual_amount == null ? null : coerceApiNumber(r.actual_amount),
    error_message: typeof r.error_message === 'string' ? r.error_message : null,
    started_at: typeof r.started_at === 'string' ? r.started_at : String(r.started_at ?? ''),
    completed_at: typeof r.completed_at === 'string' ? r.completed_at : null,
  };
}

function mapAutoBalanceConfigView(raw: unknown): AutoBalanceConfigView {
  const r = raw as Record<string, unknown>;
  return {
    enabled: Boolean(r.enabled),
    threshold_ratio: coerceApiNumber(r.threshold_ratio, 0.3),
    poll_interval_secs: Number(r.poll_interval_secs) || 30,
    cooldown_secs: Number(r.cooldown_secs) || 600,
    transfer_currency: typeof r.transfer_currency === 'string' ? r.transfer_currency : 'USDT',
    min_transfer_amount: coerceApiNumber(r.min_transfer_amount, 10),
    max_transfer_amount: coerceApiNumber(r.max_transfer_amount, 50_000),
  };
}

/** GET /fund-balances/portfolio — OKX/Bybit 双所 funding+trading 余额 */
export async function getFundPortfolio(): Promise<FundPortfolioView> {
  const res = await apiFetch(manualTradingApiUrl('/fund-balances/portfolio'));
  if (!res.ok) throw new Error(await parseApiError(res));
  const raw = (await res.json()) as { portfolio?: unknown };
  return mapFundPortfolioView(raw.portfolio ?? raw);
}

/** POST /fund-balances/transfers/internal — 所内划转 */
export async function internalFundTransfer(body: {
  exchange: string;
  currency: string;
  amount: string;
  to_funding: boolean;
}): Promise<TransferActionResponse> {
  const res = await apiFetch(manualTradingApiUrl('/fund-balances/transfers/internal'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await parseApiError(res));
  return (await res.json()) as TransferActionResponse;
}

/** POST /fund-balances/transfers/cross — 跨所划转 */
export async function crossFundTransfer(body: {
  source_exchange: string;
  target_exchange: string;
  currency: string;
  amount: number;
}): Promise<TransferActionResponse> {
  const res = await apiFetch(manualTradingApiUrl('/fund-balances/transfers/cross'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await parseApiError(res));
  return (await res.json()) as TransferActionResponse;
}

/** GET /fund-balances/transfers — 划转记录 */
export async function listFundTransfers(limit = 20): Promise<TransferRecordView[]> {
  const res = await apiFetch(manualTradingApiUrl(`/fund-balances/transfers?limit=${limit}`));
  if (!res.ok) throw new Error(await parseApiError(res));
  const raw = (await res.json()) as unknown;
  const arr = Array.isArray(raw) ? raw : [];
  return arr.map(mapTransferRecordView);
}

/** GET /fund-balances/auto-balance/status */
export async function getAutoBalanceStatus(): Promise<AutoBalanceStatusResponse> {
  const res = await apiFetch(manualTradingApiUrl('/fund-balances/auto-balance/status'));
  if (!res.ok) throw new Error(await parseApiError(res));
  const raw = (await res.json()) as Record<string, unknown>;
  return {
    config: mapAutoBalanceConfigView(raw.config),
    portfolio: raw.portfolio != null ? mapFundPortfolioView(raw.portfolio) : null,
    has_in_flight_transfer: Boolean(raw.has_in_flight_transfer),
  };
}

/** PUT /fund-balances/auto-balance/config */
export async function updateAutoBalanceConfig(
  config: AutoBalanceConfigView,
): Promise<AutoBalanceConfigView> {
  const res = await apiFetch(manualTradingApiUrl('/fund-balances/auto-balance/config'), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(config),
  });
  if (!res.ok) throw new Error(await parseApiError(res));
  return mapAutoBalanceConfigView(await res.json());
}

/** POST /fund-balances/auto-balance/run-once */
export async function runAutoBalanceOnce(): Promise<TransferActionResponse | null> {
  const res = await apiFetch(manualTradingApiUrl('/fund-balances/auto-balance/run-once'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
  if (!res.ok) throw new Error(await parseApiError(res));
  const raw = await res.json();
  if (raw == null) return null;
  return raw as TransferActionResponse;
}
